import React, { useRef } from 'react'
import { Image as ImageIcon, Camera, Paperclip, Video } from 'lucide-react'

export interface MediaPickerSheetProps {
	/** Called with the files the user chose. */
	onFiles: (files: File[]) => void
	/** Close the sheet once a source has been picked. */
	onClose: () => void
}

type Source = {
	id: string
	label: string
	hint: string
	icon: React.ComponentType<{ className?: string }>
	accept: string
	/**
	 * `capture` asks a mobile browser to open the camera directly instead of the
	 * gallery. Desktop browsers ignore it and fall back to a file dialog, which
	 * is why "Take Photo" is still listed rather than hidden.
	 */
	capture?: 'user' | 'environment'
	multiple: boolean
}

const SOURCES: Source[] = [
	{
		id: 'library',
		label: 'Photo Library',
		hint: 'Choose existing photos or videos',
		icon: ImageIcon,
		accept: 'image/*,video/*',
		multiple: true,
	},
	{
		id: 'camera',
		label: 'Take Photo',
		hint: 'Use the camera on this device',
		icon: Camera,
		accept: 'image/*',
		capture: 'environment',
		multiple: false,
	},
	{
		id: 'video',
		label: 'Record Video',
		hint: 'Use the camera on this device',
		icon: Video,
		accept: 'video/*',
		capture: 'environment',
		multiple: false,
	},
	{
		id: 'file',
		label: 'Choose File',
		hint: 'Attach a document',
		icon: Paperclip,
		accept: '*/*',
		multiple: true,
	},
]

/**
 * The three-way media source sheet, modelled on the iOS Messages "+" menu.
 *
 * Each row is a hidden `<input type="file">` with different `accept` and
 * `capture` attributes. There is no web API to open a specific system picker,
 * so these attributes are the only lever available: they change which chooser
 * the OS offers rather than guaranteeing one.
 */
const MediaPickerSheet = ({ onFiles, onClose }: MediaPickerSheetProps) => {
	const inputs = useRef<Record<string, HTMLInputElement | null>>({})

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = Array.from(e.target.files || [])
		// Reset so picking the same file twice in a row still fires onChange.
		e.target.value = ''
		if (files.length) onFiles(files)
		onClose()
	}

	return (
		<div className="w-60 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
			{SOURCES.map((source, i) => {
				const Icon = source.icon
				return (
					<React.Fragment key={source.id}>
						{i > 0 && <div className="h-px bg-gray-100" />}
						<button
							type="button"
							onClick={() => inputs.current[source.id]?.click()}
							className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50 focus:outline-none focus-visible:bg-gray-50"
						>
							<Icon className="h-5 w-5 shrink-0 text-[#0080fe]" />
							<span className="min-w-0">
								<span className="block text-sm font-medium text-gray-900">{source.label}</span>
								<span className="block text-xs text-gray-500">{source.hint}</span>
							</span>
						</button>
						<input
							ref={el => {
								inputs.current[source.id] = el
							}}
							type="file"
							accept={source.accept}
							{...(source.capture ? { capture: source.capture } : {})}
							multiple={source.multiple}
							onChange={handleChange}
							className="hidden"
						/>
					</React.Fragment>
				)
			})}
		</div>
	)
}

export default MediaPickerSheet
